import { memo, useMemo, useState, MouseEvent } from 'react';
import { IBotSection } from '../../models';
import { AccessoryDetailComponent } from '../../models/bot';
import AccessDoorDetails from './access_door';
import OpeningDetails from './opening';
import DamperDetails from './damper';
import DrainPanDetails from './drain_pan';
import AccessoryDetailFallback from './detail_fallback';
import Button from '../atom/Button';

const ACCESSORY_COMPONENTS: Record<string, AccessoryDetailComponent> = {
  access_door: AccessDoorDetails,
  opening: OpeningDetails,
  damper: DamperDetails,
  drain_pan: DrainPanDetails,
};

const AccessoryItem = ({
  acc,
  idx,
  onRemove,
  onLocationChange,
  isLastSection = false,
  section,
  onSectionFieldChange,
  onAccessoryFieldChange,
  tunnelIndex,
  airflow_direction_id,
  tunnel_configuration,
}: {
  acc: IBotSection['accessories'][0];
  idx: number;
  onRemove: (idx: number) => void;
  onLocationChange: (idx: number, loc: any) => void;
  isLastSection?: boolean;
  section: IBotSection;
  onSectionFieldChange?: (value: string | number | null, name: string) => void;
  onAccessoryFieldChange?: (
    idx: number,
    value: string | number | null,
    name: string,
  ) => void;
  tunnelIndex: number;
  airflow_direction_id?: number;
  tunnel_configuration: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const DetailComponent = useMemo(() => {
    return ACCESSORY_COMPONENTS[acc.type] || AccessoryDetailFallback;
  }, [acc.type]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRemove(idx);
  };

  const handleLocationChange = (value: any) => {
    onLocationChange(idx, value);
  };

  // Hide remove button for opening accessory on last section
  // Hide remove button for drain pan accessory when type_id === 1 for coil sections
  const canRemove = !(
    (isLastSection && acc.type === 'opening') ||
    (section.type === 'coil' &&
      section.type_id === 1 &&
      acc.type === 'drain_pan')
  );

  return (
    <li className={`bot-accessories__item ${isOpen ? 'is-open' : ''}`}>
      <div className="bot-accessories__item-row">
        <button
          type="button"
          className="bot-accessories__item-header"
          onClick={handleToggle}
        >
          <span className="bot-accessories__item-name">{acc.name}</span>
          <span
            className={`bot-accessories__chevron ${
              isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
            }`}
            aria-hidden
          />
        </button>
        <Button
          type="button"
          className="bot-accessories__remove bg-transparent"
          aria-label="Remove accessory"
          onClick={handleRemove}
          disabled={!canRemove}
          hasShadow={false}
        >
          <span className="icon-trash-o" aria-hidden />
        </Button>
      </div>
      {isOpen && (
        <div className="bot-accessories__details">
          <DetailComponent
            accessory={acc}
            onLocationChange={handleLocationChange}
            onSectionFieldChange={onSectionFieldChange}
            onAccessoryFieldChange={
              onAccessoryFieldChange
                ? (value: string | number | null, name: string) =>
                    onAccessoryFieldChange(idx, value, name)
                : undefined
            }
            tunnelIndex={tunnelIndex}
            airflow_direction_id={airflow_direction_id}
            tunnel_configuration={tunnel_configuration}
          />
        </div>
      )}
    </li>
  );
};

export default memo(AccessoryItem);
